import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import ReportLayout from '../../components/reports/ReportLayout';
import FinancialReportSection, {
  FinancialReportRow
} from '../../components/reports/FinancialReportSection';
import ReportNote from '../../components/reports/ReportNote';
import { useClient } from '../../context/ClientContext';
import { arAgingService } from '../../services/arAgingService';
import { formatCurrency, formatDate } from '../../utils/formatters';
import {
  downloadARAgingCsv,
  downloadARAgingPdf
} from '../../utils/arAgingExport';

const { FiChevronDown, FiChevronRight, FiExternalLink } = FiIcons;

const bucketRows = [
  ['current', 'Current'],
  ['1-30', '1–30 Days'],
  ['31-60', '31–60 Days'],
  ['61-90', '61–90 Days'],
  ['91+', '91+ Days']
];

const getInvoiceCount = (customers, bucket) => customers.reduce(
  (count, customer) => count + customer.invoices.filter(
    (invoice) => invoice.bucket === bucket
  ).length,
  0
);

function InvoiceDetails({ invoices }) {
  return (
    <div className="mb-3 border-t border-slate-100 pt-2">
      <div className="hidden grid-cols-5 gap-4 border-b border-slate-100 px-1 py-2 text-[10px] font-medium uppercase tracking-wider text-slate-400 sm:grid">
        <span>Invoice</span>
        <span>Invoice Date</span>
        <span>Due Date</span>
        <span className="text-right">Outstanding</span>
        <span className="text-right">Aging Bucket</span>
      </div>

      {invoices.map((invoice) => {
        const bucketLabel = bucketRows.find(
          ([key]) => key === invoice.bucket
        )?.[1] || 'Current';

        return (
          <div
            key={invoice.id}
            className="grid grid-cols-2 gap-x-4 gap-y-1 border-b border-slate-100 px-1 py-2 text-xs sm:grid-cols-5"
          >
            <span className="font-medium text-slate-700">{invoice.number}</span>
            <span className="text-slate-500">{formatDate(invoice.issueDate)}</span>
            <span className="text-slate-500">{formatDate(invoice.dueDate)}</span>
            <span className="text-right font-medium tabular-nums text-slate-700">
              {formatCurrency(invoice.balance)}
            </span>
            <span className="text-right text-slate-500">{bucketLabel}</span>
          </div>
        );
      })}
    </div>
  );
}

function CustomerAgingSection({
  customer,
  expanded,
  onExpand,
  onNavigate
}) {
  return (
    <FinancialReportSection
      title={customer.name}
      totalLabel="Total"
      total={customer.total}
      emptyLabel=""
      rows={
        <>
          {bucketRows.map(([key, label]) => (
            <FinancialReportRow
              key={key}
              label={label}
              value={customer.buckets[key]}
            />
          ))}

          <div className="flex items-center justify-between py-3">
            <button
              type="button"
              onClick={onExpand}
              className="flex items-center gap-1 text-xs font-medium text-blue-700 transition hover:text-blue-800"
            >
              <SafeIcon icon={expanded ? FiChevronDown : FiChevronRight} />
              {expanded ? 'Hide invoices' : 'View invoices'}
            </button>

            {customer.id && !String(customer.id).startsWith('unassigned-') && (
              <button
                type="button"
                onClick={onNavigate}
                className="flex items-center gap-1 text-xs font-medium text-slate-500 transition hover:text-blue-700"
              >
                Customer profile
                <SafeIcon icon={FiExternalLink} className="text-[11px]" />
              </button>
            )}
          </div>

          {expanded && <InvoiceDetails invoices={customer.invoices} />}
        </>
      }
    />
  );
}

export default function ARAgingReport() {
  const navigate = useNavigate();
  const { activeClient } = useClient();
  const [asOfDate, setAsOfDate] = useState(
    new Date().toISOString().split('T')[0]
  );
  const [viewMode, setViewMode] = useState('details');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exportOpen, setExportOpen] = useState(false);
  const [expandedCustomer, setExpandedCustomer] = useState(null);
  const [error, setError] = useState(null);

  const fetchReport = async () => {
    if (!activeClient?.id) {
      setReport(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await arAgingService.generate(asOfDate, activeClient.id);
      setReport(data);
      setExpandedCustomer(null);
    } catch (fetchError) {
      console.error(fetchError);
      setError(fetchError.message || 'Failed to generate AR Aging report');
      setReport(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, [activeClient?.id, asOfDate]);

  const bucketCounts = useMemo(() => {
    const customers = report?.customers || [];

    return bucketRows.reduce((counts, [key]) => ({
      ...counts,
      [key]: getInvoiceCount(customers, key)
    }), {});
  }, [report]);

  const totalOverdueAmount = useMemo(() => (
    bucketRows
      .filter(([key]) => key !== 'current')
      .reduce(
        (total, [key]) => total + Number(report?.grandTotals?.buckets[key] || 0),
        0
      )
  ), [report]);

  const handleExport = (format) => {
    if (!report) return;

    const options = {
      report,
      viewMode: 'details',
      asOfDate
    };

    if (format === 'csv') {
      downloadARAgingCsv(options);
    } else {
      downloadARAgingPdf(options);
    }

    setExportOpen(false);
  };

  const toggleCustomer = (customerId) => {
    setExpandedCustomer((current) => (
      current === customerId ? null : customerId
    ));
  };

  void bucketCounts;
  void totalOverdueAmount;
  void CustomerAgingSection;
  void toggleCustomer;
  void ReportNote;

  return (
    <ReportLayout
      title="Accounts Receivable Aging"
      description="Outstanding customer balances organized by aging period."
      viewMode={viewMode}
      setViewMode={setViewMode}
      endDate={asOfDate}
      onEndDateChange={setAsOfDate}
      onRefresh={fetchReport}
      loading={loading}
      onExport={handleExport}
      exportOpen={exportOpen}
      setExportOpen={setExportOpen}
      asOfOnly
      showTabs={false}
    >
      {!activeClient && (
        <p className="py-10 text-center text-sm text-slate-500">
          Select a business to view accounts receivable.
        </p>
      )}

      {error && (
        <div className="mb-4 flex items-center justify-between rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
          <span>{error}</span>
          <button
            type="button"
            onClick={fetchReport}
            className="font-medium underline"
          >
            Try again
          </button>
        </div>
      )}

      {report && viewMode === 'summary' && (
        <div>
          <FinancialReportSection
            title="Aging Summary"
            totalLabel="Total Receivables"
            total={report.grandTotals.total}
            emptyLabel=""
            rows={bucketRows.map(([key, label]) => (
              <div
                key={key}
                className="flex min-h-10 items-center justify-between border-b border-slate-100 py-2 text-sm text-slate-700"
              >
                <span>{label}</span>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-slate-400">
                    {bucketCounts[key]} invoice{bucketCounts[key] === 1 ? '' : 's'}
                  </span>
                  <span className="font-medium tabular-nums text-slate-900">
                    {formatCurrency(report.grandTotals.buckets[key])}
                  </span>
                </div>
              </div>
            ))}
          />

          <div className="px-1 text-xs text-slate-500">
            {formatCurrency(totalOverdueAmount)} is currently overdue.
          </div>
        </div>
      )}

      {report && viewMode === 'details' && (
        <div>
          {report.customers.length > 0 ? (
            <>
              {report.customers.map((customer) => (
                <CustomerAgingSection
                  key={customer.id}
                  customer={customer}
                  expanded={expandedCustomer === customer.id}
                  onExpand={() => toggleCustomer(customer.id)}
                  onNavigate={() => navigate(`/customers/${customer.id}`)}
                />
              ))}

              <FinancialReportRow
                label="Grand Total"
                value={report.grandTotals.total}
                total
                grand
              />
            </>
          ) : (
            <p className="py-10 text-center text-sm text-slate-500">
              {report.emptyReason || 'No outstanding receivables found for this date.'}
            </p>
          )}
        </div>
      )}

      {!loading && activeClient && !report && !error && (
        <p className="py-10 text-center text-sm text-slate-500">
          No report data is available.
        </p>
      )}

      {report && (
        <div className="mt-8 opacity-70">
          <ReportNote />
        </div>
      )}
    </ReportLayout>
  );
}